// Customer Statement DTOs (PD-13702 / PD-14041) — request + response contracts
// for the crm-api statements endpoints, shared with crm-web per CLAUDE.md so
// both sides type-check against the same shape.

import { Type } from 'class-transformer';
import {
	ArrayNotEmpty,
	IsArray,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Max,
	Min,
	Validate,
} from 'class-validator';
import type { StatementRunEntity } from '../entity/statement-run.entity';
import type { StatementEntity } from '../entity/statement.entity';
import type { CustomerStatementRequestModel } from '../interfaces';
import { IsEmailAddressDtoOrString } from './send-email.dto';

export class GetCustomerStatementDto implements CustomerStatementRequestModel {
	@IsInt()
	public clientId!: number;

	@IsString()
	@IsOptional()
	public periodStart?: string | null;

	@IsString()
	@IsOptional()
	public periodEnd?: string | null;

	@IsString()
	@IsOptional()
	public asOfDate?: string | null;
}

export class EmailCustomerStatementDto extends GetCustomerStatementDto {
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public to!: Array<string>;

	@IsArray()
	@IsOptional()
	public cc?: Array<string>;

	@IsArray()
	@IsOptional()
	public bcc?: Array<string>;

	@IsString()
	@IsOptional()
	public subject?: string;

	/** Optional intro paragraph rendered above the summary in the email body. */
	@IsString()
	@IsOptional()
	public message?: string;
}

// ---------------------------------------------------------------------------
// Stored statement records (PD-14041). POST /api/statements/generate reuses
// GetCustomerStatementDto — generation takes exactly the preview parameters
// (an empty subclass would only add lint noise); these cover the list/read
// side of the stored records.
// ---------------------------------------------------------------------------

/** Query params for GET /api/statements (paginated stored-statement list). */
export class ListCustomerStatementsQueryDto {
	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@IsOptional()
	public page: number = 1;

	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@Max(100)
	@IsOptional()
	public limit: number = 50;

	@Type(() => Number)
	@IsNumber()
	@IsOptional()
	public clientId?: number;

	/** Issuing company; validated against the caller's accessible companies. */
	@Type(() => Number)
	@IsNumber()
	@IsOptional()
	public companyId?: number;

	/** Inclusive lower bound on the statement as-of date (ISO YYYY-MM-DD). */
	@IsString()
	@IsOptional()
	public asOfDateStart?: string;

	/** Inclusive upper bound on the statement as-of date (ISO YYYY-MM-DD). */
	@IsString()
	@IsOptional()
	public asOfDateEnd?: string;
}

/**
 * One row in the stored-statements list — everything a list screen needs
 * WITHOUT the (potentially large) JSON snapshot. Derived from the entity per
 * the shared-DTO rule; `statementNumber` (`ST-<id>`) and `clientName` are
 * computed/joined server-side rather than stored.
 */
export type CustomerStatementRecordListItemDto = Pick<
	StatementEntity,
	| 'id'
	| 'fkClientId'
	| 'fkCompanyId'
	| 'asOfDate'
	| 'periodStartDate'
	| 'periodEndDate'
	| 'endingBalance'
	| 'totalAmountDue'
	| 'generationSource'
	| 'distributedDate'
	| 'createdDate'
> & {
	/** Derived human reference (`ST-<id>`); not a stored column. */
	statementNumber: string;
	clientName: string;
};

/** A full stored statement record — the list row plus the rendered snapshot. */
export type CustomerStatementRecordDto = CustomerStatementRecordListItemDto &
	Pick<StatementEntity, 'snapshotJson'>;

// ---------------------------------------------------------------------------
// Statement runs (PD-14044). POST /api/statements/runs queues one PENDING run
// per requested company; the BullMQ run processor picks pending runs up on its
// next tick and generates the statements.
// ---------------------------------------------------------------------------

/** Body for POST /api/statements/runs ("Add Statement Run"). */
export class CreateStatementRunDto {
	/** Issuing companies; validated against the caller's accessible companies. */
	@IsArray()
	@ArrayNotEmpty()
	@IsInt({ each: true })
	public companyIds!: Array<number>;

	@IsString()
	@IsOptional()
	public periodStart?: string | null;

	@IsString()
	@IsOptional()
	public periodEnd?: string | null;

	/** Defaults to today — the run's point-in-time stamp. */
	@IsString()
	@IsOptional()
	public asOfDate?: string | null;
}

/** One statement run row, as served by GET /api/statements/runs. */
export type StatementRunDto = Pick<
	StatementRunEntity,
	| 'id'
	| 'fkCompanyId'
	| 'fkClientId'
	| 'asOfDate'
	| 'periodStartDate'
	| 'periodEndDate'
	| 'status'
	| 'source'
	| 'statementsGenerated'
	| 'statementsSkipped'
	| 'statementsDistributed'
	| 'completedDate'
	| 'errorMessage'
	| 'createdDate'
> & {
	companyName: string;
};
